// Offline-first, server-mirrored contacts bundle hook.
//
// Read path:  reads `settings_kv['contacts.bundle.v1']` on mount to immediately
//             populate the list from the last known-good server response (no
//             spinner on reload), then background-fetches GET /v1/contacts.
// Write path: mutations optimistically update local state, call the real
//             endpoint (migration 0029), and reconcile from the server. Because
//             the list is now server-backed, favourites + folders SYNC across
//             the account's devices.
//
// Contacts from existing 1:1 conversations + device contacts are merged in by
// ContactsRouteHost (both already offline-first); this hook owns the saved
// contact list, favourites, and folders.

import { useCallback, useEffect, useMemo, useState } from 'react';

import { log } from '@core/log/log';
import {
  addContact as netAddContact,
  type ContactFolder,
  type ContactsBundle,
  createFolder as netCreateFolder,
  deleteFolder as netDeleteFolder,
  loadContactsBundle,
  removeContact as netRemoveContact,
  setFavourite as netSetFavourite,
} from '@core/network/contacts';
import { db } from '@core/persistence/db';

const CACHE_KEY = 'contacts.bundle.v1';

interface UseContactsBundleResult {
  bundle: ContactsBundle;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  addContact: (userId: string) => Promise<void>;
  removeContact: (userId: string) => Promise<void>;
  toggleFavourite: (userId: string) => Promise<void>;
  createFolder: (name: string, contactIds?: readonly string[]) => Promise<ContactFolder>;
  deleteFolder: (folderId: string) => Promise<void>;
}

const EMPTY: ContactsBundle = {
  contacts: [],
  favouriteIds: [],
  folders: [],
  pendingRequestCount: 0,
};

function isContactsBundle(v: unknown): v is ContactsBundle {
  return (
    v !== null &&
    typeof v === 'object' &&
    Array.isArray((v as ContactsBundle).contacts) &&
    Array.isArray((v as ContactsBundle).favouriteIds)
  );
}

export function useContactsBundle(): UseContactsBundleResult {
  const [bundle, setBundle] = useState<ContactsBundle>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Restore from cache on mount (single IDB get).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const row = await db.settings_kv.get(CACHE_KEY);
        if (cancelled) return;
        if (row !== undefined && isContactsBundle(row.value)) {
          setBundle(row.value);
          setIsLoading(false);
        }
      } catch {
        // Cache miss — network fetch below populates.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchAndCache = useCallback(async (signal?: AbortSignal) => {
    try {
      const next = await loadContactsBundle(signal);
      if (signal?.aborted) return;
      setBundle(next);
      setIsLoading(false);
      await db.settings_kv.put({ key: CACHE_KEY, value: next, updatedAt: Date.now() });
    } catch (err) {
      if (signal?.aborted) return;
      log.error({ event: 'contacts.load.failed', err: (err as Error).message });
      setError(err as Error);
      setIsLoading(false);
    }
  }, []);

  // Background network sync on mount.
  useEffect(() => {
    const c = new AbortController();
    void fetchAndCache(c.signal);
    return () => c.abort();
  }, [fetchAndCache]);

  const refresh = useCallback(async () => {
    await fetchAndCache();
  }, [fetchAndCache]);

  const addContact = useCallback(
    async (userId: string) => {
      await netAddContact({ userId }).catch((err) => {
        log.warn({ event: 'contacts.add.failed', err: (err as Error).message });
      });
      await fetchAndCache();
    },
    [fetchAndCache],
  );

  const removeContact = useCallback(
    async (userId: string) => {
      // Optimistic removal from every derived slice.
      setBundle((b) => ({
        ...b,
        contacts: b.contacts.filter((u) => u.id !== userId),
        favouriteIds: b.favouriteIds.filter((id) => id !== userId),
        folders: b.folders.map((f) => ({
          ...f,
          contactIds: f.contactIds.filter((id) => id !== userId),
        })),
      }));
      await netRemoveContact({ userId }).catch((err) => {
        log.warn({ event: 'contacts.remove.failed', err: (err as Error).message });
      });
      await fetchAndCache();
    },
    [fetchAndCache],
  );

  const toggleFavourite = useCallback(
    async (userId: string) => {
      // Compute the desired state from the current one, optimistically apply,
      // then persist the explicit (idempotent) state to the server.
      let nextFavourite = false;
      setBundle((b) => {
        const has = b.favouriteIds.includes(userId);
        nextFavourite = !has;
        return {
          ...b,
          favouriteIds: has
            ? b.favouriteIds.filter((id) => id !== userId)
            : [...b.favouriteIds, userId],
        };
      });
      await netSetFavourite({ userId, favourite: nextFavourite }).catch((err) => {
        log.warn({ event: 'contacts.favourite.failed', err: (err as Error).message });
      });
    },
    [],
  );

  const createFolder = useCallback(
    async (name: string, contactIds: readonly string[] = []) => {
      const folder = await netCreateFolder({ name, contactIds });
      setBundle((b) => ({ ...b, folders: [...b.folders, folder] }));
      return folder;
    },
    [],
  );

  const deleteFolder = useCallback(async (folderId: string) => {
    setBundle((b) => ({ ...b, folders: b.folders.filter((f) => f.id !== folderId) }));
    await netDeleteFolder({ folderId }).catch((err) => {
      log.warn({ event: 'contacts.deleteFolder.failed', err: (err as Error).message });
    });
  }, []);

  return useMemo(
    () => ({
      bundle,
      isLoading,
      error,
      refresh,
      addContact,
      removeContact,
      toggleFavourite,
      createFolder,
      deleteFolder,
    }),
    [
      bundle,
      isLoading,
      error,
      refresh,
      addContact,
      removeContact,
      toggleFavourite,
      createFolder,
      deleteFolder,
    ],
  );
}
